import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from "@angular/common";

export enum LANGS {
  EN = 'en',
  NO = 'no'
}

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private _translateService: TranslateService = inject(TranslateService);
  private _router: Router = inject(Router);
  private _platformId = inject(PLATFORM_ID);
  private _document = inject(DOCUMENT);

  supportedLangs: string[] = [LANGS.EN, LANGS.NO];
  defaultLang: LANGS.EN | LANGS.NO = LANGS.NO;

  constructor() {}

  initLanguageFromUrl(): Promise<string> {
    return new Promise((resolve) => {
      const lang: string = this.getLangFromUrl();
      this.setLanguage(lang);
      resolve(lang);
    });
  }

  private getLangFromUrl(): string {
    if (!isPlatformBrowser(this._platformId)) {
      return this.defaultLang;
    }

    const path = this._document.defaultView?.location?.pathname ?? '/';
    const firstSegment = path.split('/')[1] ?? '';

    return this.supportedLangs.indexOf(firstSegment) !== -1
      ? firstSegment
      : this.defaultLang;
  }

  setLanguage(lang: string, fromNav?: boolean): void {
    if (this._translateService.currentLang === lang) {
      return;
    }

    if (this.supportedLangs.indexOf(lang) === -1) {
      lang = this.defaultLang;
    }

    this._translateService.use(lang);

    if (fromNav) {
      this.updateRouteLangPrefix(lang);
    }
  }

  updateRouteLangPrefix(newLang: string): void {
    const urlSegments = this._router.url.split('/');
    const currentLangInUrl = urlSegments[1];

    // Only update if the first segment is a supported language
    if (this.supportedLangs.indexOf(currentLangInUrl) !== -1) {
      urlSegments[1] = newLang;
      const newUrl = urlSegments.join('/');
      this._router.navigateByUrl(newUrl);
    }
  }

  get currentLang(): string {
    return this._translateService.currentLang || this.defaultLang;
  }

  get onLangChange$() {
    return this._translateService.onLangChange;
  }
}
